import React from 'react'
// eslint-disable-next-line no-restricted-imports -- Platform for web/mobile type-scale + weight divergence
import { Platform } from 'react-native'

import { LocationPinIcon, TimeIcon } from '../../icons'
import { Box } from '../box'
import { HStack } from '../hstack'
import { Icon } from '../icon'
import { Pressable } from '../pressable'
import { Text } from '../text'
import { VStack } from '../vstack'

// The host Tailwind build does not emit NativeWind `web:` platform-variant
// utilities, so web/mobile divergence is driven from JS (`Platform.OS`) instead.
const IS_WEB = Platform.OS === 'web'

export interface NotificationListItemIconProps {
    color: string
    variant?: 'filled' | 'bordered'
    showDot?: boolean
    children: React.ReactNode
}

export const NotificationListItemIcon: React.FC<NotificationListItemIconProps> = ({
    color,
    variant = 'filled',
    showDot = false,
    children,
}) => (
    <Box
        className={`items-center justify-center ${
            variant === 'bordered' ? 'rounded-[5px]' : 'rounded-[4px]'
        } ${IS_WEB ? 'h-10 w-10' : 'h-6 w-6'}`}
        style={
            variant === 'filled'
                ? { backgroundColor: color }
                : {
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: color,
                  }
        }
    >
        {children}
        {variant === 'bordered' && showDot ? (
            <Box
                className='absolute'
                style={{
                    top: 3,
                    left: 3,
                    width: 4,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: color,
                }}
            />
        ) : null}
    </Box>
)

export interface NotificationListItemProps {
    icon: React.ReactNode
    title: React.ReactNode
    body?: React.ReactNode
    time?: string
    location?: string
    action?: React.ReactNode
    unread?: boolean
    onPress?: () => void
}

export const NotificationListItem: React.FC<NotificationListItemProps> = ({
    icon,
    title,
    body,
    time,
    location,
    action,
    unread,
    onPress,
}) => {
    const textScale = IS_WEB ? 'text-[0.875rem] leading-[1.25rem]' : 'text-[16px] leading-[24px]'
    const metaTextClass = `text-content-default ${textScale}`
    const content = (
        <Box className='border-b border-border-strong border-solid'>
            <HStack className={`items-start p-4 ${IS_WEB ? 'gap-3' : 'gap-4'}`}>
                <Box className='shrink-0'>{icon}</Box>
                <VStack className={`min-w-0 flex-1 ${IS_WEB ? 'gap-2' : 'gap-1'}`}>
                    <Text weight='medium' className={`pr-4 ${metaTextClass}`}>
                        {title}
                    </Text>
                    {body ? (
                        <Text numberOfLines={3} className={`text-content-muted ${textScale}`}>
                            {body}
                        </Text>
                    ) : null}
                    {action ? <Box className='self-start'>{action}</Box> : null}
                    <HStack className={`flex-wrap items-center gap-2 ${IS_WEB ? '' : 'pb-1 pt-1'}`}>
                        {time ? (
                            <HStack className='items-center gap-2'>
                                <Icon as={TimeIcon} size={24} className='text-content-default' />
                                <Text weight='medium' className={metaTextClass}>
                                    {time}
                                </Text>
                            </HStack>
                        ) : null}
                        {location ? (
                            <HStack className='items-center gap-2'>
                                <Icon
                                    as={LocationPinIcon}
                                    size={24}
                                    className='text-content-default'
                                />
                                <Text weight='medium' className={metaTextClass}>
                                    {location}
                                </Text>
                            </HStack>
                        ) : null}
                    </HStack>
                </VStack>
                {unread ? (
                    <Box className='absolute right-4 top-4 h-2 w-2 rounded-full bg-action-default' />
                ) : null}
            </HStack>
        </Box>
    )

    if (!onPress) {
        return content
    }
    return (
        // `accessibilityRole` earns the item its announcement, keyboard activation and
        // focus ring — react-native-web makes it a tab stop either way, so without the
        // role it was an unlabeled stop with no visible focus. Safe here: the item wraps
        // no other pressable, so no button ends up nested in a button.
        <Pressable
            onPress={onPress}
            accessibilityRole='button'
            className='hover:bg-background-surface-hover'
        >
            {content}
        </Pressable>
    )
}

export interface NotificationDateHeaderProps {
    children: React.ReactNode
}

export const NotificationDateHeader: React.FC<NotificationDateHeaderProps> = ({ children }) => (
    <Box className='justify-center border-b border-border-strong border-solid p-4'>
        <Text
            weight={IS_WEB ? 'regular' : 'medium'}
            className={`text-content-default ${IS_WEB ? 'text-[18px] leading-[25px]' : 'text-[16px] leading-[24px]'}`}
        >
            {children}
        </Text>
    </Box>
)
